/**
 * @file     PanelIntroMode.tsx
 * @purpose  Client islands for the /properties PanelIntro: the Buy | Rent
 *           control and the eyebrow and intro line that follow the mode. The
 *           live map view supplies the mode (SearchModeProvider); before it
 *           mounts, the page decides (/rent or /properties) and a switch opens
 *           the other page.
 */
'use client';

import { usePathname, useRouter } from 'next/navigation';
import { SearchModeControl, useSearchMode } from '@/components/search/SearchModeControl';
import { isRentSearchPath, searchPathFor, type SearchMode } from '@/components/search/searchModeSwitch';

function useUrlMode(): SearchMode {
  return isRentSearchPath(usePathname()) ? 'rent' : 'buy';
}

/** The mode the panel is showing: the live map state, else the page. */
export function usePanelMode(): SearchMode {
  const live = useSearchMode();
  const urlMode = useUrlMode();
  return live?.mode ?? urlMode;
}

export function PanelModeControl() {
  const live = useSearchMode();
  const urlMode = useUrlMode();
  const router = useRouter();
  const mode = live?.mode ?? urlMode;
  const onChange = (next: SearchMode) => {
    if (live) {
      live.setMode(next);
      return;
    }
    router.push(searchPathFor(next));
  };
  return <SearchModeControl mode={mode} onChange={onChange} surface="properties" />;
}

/** Text that reads one way for Buy and another for Rent. Both strings come resolved from the server. */
export function ModeText({ sale, rent }: { sale: string; rent: string }) {
  return <>{usePanelMode() === 'rent' ? rent : sale}</>;
}
